import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight, KeyRound, ShieldX, Clock, Phone } from "lucide-react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Setup() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null); // null=loading, false=invalid, {email,name}
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingMsg, setPendingMsg] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setInfo(false); return; }
    axios.get(`${API_ROOT}/auth/setup-info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(false));
  }, [token]);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/auth/setup-password`, { token, password: password.trim() });
      setPendingMsg(data.message || "Thank you for joining our team. Please wait for admin to complete your sign-in request, or call Allan on +61 404 213 704.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password — the link may have expired");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-10 lg:px-16 bg-white relative">
        <div className="absolute top-6 left-6 lg:left-16 flex items-center gap-3">
          <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-16 h-16 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-bold text-slate-900 text-base">CCFI <span className="text-cyan-600">CRM</span></div>
            <div className="text-[10px] text-slate-500 stamp-label mt-0.5">Account Activation</div>
          </div>
        </div>
        <div className="w-full max-w-sm">
          {pendingMsg ? (
            <div data-testid="setup-pending-approval">
              <div className="stamp-label text-amber-600 mb-3 flex items-center gap-1.5"><Clock size={12} /> Awaiting approval</div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Thank you for joining our team.</h1>
              <div className="mt-6 border border-amber-200 bg-amber-50 rounded-sm p-4 text-sm text-amber-900" data-testid="setup-pending-message">
                {pendingMsg}
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                <Phone size={14} className="text-cyan-600" />
                <a href="tel:+61404213704" className="font-semibold text-cyan-700">+61 404 213 704</a>
                <span className="text-slate-400">— Allan</span>
              </div>
              <p className="text-xs text-slate-400 mt-4">Once approved, sign in with your email and the password you just created.</p>
              <Button onClick={() => nav("/login")} variant="outline" className="mt-4 rounded-sm h-11" data-testid="setup-goto-login-btn">Go to sign in</Button>
            </div>
          ) : (
            <>
              {info === null && (
                <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={16} className="animate-spin" /> Checking your setup link…</div>
              )}

              {info === false && (
                <div data-testid="setup-invalid">
                  <div className="stamp-label text-rose-600 mb-3">Invalid link</div>
                  <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Link expired or used.</h1>
                  <div className="mt-6 border border-rose-200 bg-rose-50 rounded-sm p-4 flex items-start gap-3">
                    <ShieldX size={20} className="text-rose-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-rose-900">This activation link is invalid or has already been used. Ask your administrator to send you a fresh setup link.</div>
                  </div>
                  <Button onClick={() => nav("/login")} variant="outline" className="mt-4 rounded-sm h-11">Go to sign in</Button>
                </div>
              )}

              {info && info.email && (
                <>
                  <div className="stamp-label text-emerald-600 mb-3 flex items-center gap-1.5"><KeyRound size={12} /> Activate your account</div>
                  <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Welcome{info.name ? `, ${info.name.split(" ")[0]}` : ""}.</h1>
                  <p className="text-sm text-slate-500 mt-2">
                    <span className="font-mono">{info.email}</span><br />
                    Create your own password. An admin will then approve your access — you&apos;ll be able to sign in once that&apos;s done.
                  </p>
                  <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="setup-form">
                    <div>
                      <Label className="stamp-label text-slate-700">Create password</Label>
                      <Input data-testid="setup-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus className="mt-1.5 h-11 rounded-sm" autoComplete="new-password" />
                      <div className="text-[10px] text-slate-500 mt-1">Min 10 characters with an UPPERCASE letter, lowercase letter and a number.</div>
                    </div>
                    <div>
                      <Label className="stamp-label text-slate-700">Confirm password</Label>
                      <Input data-testid="setup-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 h-11 rounded-sm" autoComplete="new-password" />
                    </div>
                    <Button data-testid="setup-submit-btn" type="submit" disabled={submitting} className="w-full h-11 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold tracking-tight">
                      {submitting ? <Loader2 className="animate-spin" size={18} /> : (<>Set my password <ArrowRight size={16} className="ml-1" /></>)}
                    </Button>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:flex relative bg-slate-900 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1455165814004-1126a7199f9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBmYWJyaWNhdGlvbiUyMHdlbGRpbmd8ZW58MHx8fHwxNzgyNjA2NDM0fDA&ixlib=rb-4.1.0&q=85" alt="Steel fabrication" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/40 to-cyan-900/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white w-full">
          <div className="stamp-label text-cyan-400 mb-3">EST. CENTRAL COAST NSW</div>
          <h2 className="font-display text-5xl xl:text-6xl font-bold tracking-tight leading-[0.95] max-w-md">Welcome to<br/>the crew.<span className="text-cyan-500">_</span></h2>
        </div>
      </div>
    </div>
  );
}
